import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { SearchParams, SearchResponse } from '../types';

// SAM.gov API Configuration
// For development, we call the API directly. In production, use Firebase Functions.
const SAM_API_KEY = 'SAM-cee4dc72-436c-42f5-99af-6a8efca2ae82';
const SAM_API_URL = 'https://api.sam.gov/opportunities/v2/search';

export async function searchOpportunities(params: SearchParams): Promise<SearchResponse> {
    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('api_key', SAM_API_KEY);
    queryParams.append('limit', String(params.limit || 25));

    if (params.postedFrom) {
        // Convert YYYY-MM-DD to MM/DD/YYYY format required by SAM.gov
        const [y, m, d] = params.postedFrom.split('-');
        queryParams.append('postedFrom', `${m}/${d}/${y}`);

        // SAM.gov v2 API requires postedTo when postedFrom is used
        const today = new Date();
        const toMonth = String(today.getMonth() + 1).padStart(2, '0');
        const toDay = String(today.getDate()).padStart(2, '0');
        const toYear = today.getFullYear();
        queryParams.append('postedTo', `${toMonth}/${toDay}/${toYear}`);
    }

    if (params.title) {
        queryParams.append('title', params.title);
    }

    if (params.naicsCode) {
        queryParams.append('ncode', params.naicsCode);
    }

    if (params.typeOfSetAside) {
        queryParams.append('typeOfSetAside', params.typeOfSetAside);
    }

    if (params.zipCode) queryParams.append('zip', params.zipCode);
    if (params.city) queryParams.append('city', params.city);
    if (params.state) queryParams.append('state', params.state);

    const url = `${SAM_API_URL}?${queryParams.toString()}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('SAM.gov API Error:', response.status, errorText);
            throw new Error(`SAM.gov API Error: ${response.status}. ${errorText}`);
        }

        const data = await response.json();

        if (data.error) {
            throw new Error(data.error.message || data.error);
        }

        return {
            opportunitiesData: data.opportunitiesData || [],
            totalRecords: data.totalRecords || data.opportunitiesData?.length || 0
        };
    } catch (error: any) {
        // If CORS error, try via proxy
        if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
            console.log('Direct API failed, trying CORS proxy...');
            return searchOpportunitiesViaProxy(params);
        }
        throw error;
    }
}

// Fallback: Use a CORS proxy for development
async function searchOpportunitiesViaProxy(params: SearchParams): Promise<SearchResponse> {
    // Use allorigins.win as a CORS proxy
    const queryParams = new URLSearchParams();
    queryParams.append('api_key', SAM_API_KEY);
    queryParams.append('limit', String(params.limit || 25));

    if (params.postedFrom) {
        const [y, m, d] = params.postedFrom.split('-');
        queryParams.append('postedFrom', `${m}/${d}/${y}`);
    }
    if (params.title) queryParams.append('title', params.title);
    if (params.naicsCode) queryParams.append('ncode', params.naicsCode);
    if (params.typeOfSetAside) queryParams.append('typeOfSetAside', params.typeOfSetAside);
    if (params.zipCode) queryParams.append('zip', params.zipCode);
    if (params.city) queryParams.append('city', params.city);
    if (params.state) queryParams.append('state', params.state);

    const targetUrl = `${SAM_API_URL}?${queryParams.toString()}`;
    const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(targetUrl)}`;

    const response = await fetch(proxyUrl);

    if (!response.ok) {
        throw new Error(`Proxy Error: ${response.status}`);
    }

    const proxyData = await response.json();

    if (proxyData.status && proxyData.status.http_code !== 200) {
        throw new Error(`SAM.gov Error: ${proxyData.status.http_code}`);
    }

    const data = JSON.parse(proxyData.contents);

    return {
        opportunitiesData: data.opportunitiesData || [],
        totalRecords: data.totalRecords || data.opportunitiesData?.length || 0
    };
}

// Firebase Functions version (will be used after deployment)
export async function searchOpportunitiesFirebase(params: SearchParams): Promise<SearchResponse> {
    const searchFn = httpsCallable<SearchParams, SearchResponse>(functions, 'searchOpportunities');
    const result = await searchFn(params);
    return result.data;
}

// Format date for display
export function formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

// Check if a contract is closing soon (within 7 days)
export function isClosingSoon(deadlineString?: string): boolean {
    if (!deadlineString) return false;
    const deadline = new Date(deadlineString);
    const now = new Date();
    const diffDays = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
}

// Check if a contract was posted recently (within 3 days)
export function isNew(postedDateString: string): boolean {
    const postedDate = new Date(postedDateString);
    const now = new Date();
    const diffDays = Math.ceil((now.getTime() - postedDate.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= 3;
}
